// components/OneTimeExpensesCard/OneTimeExpensesCard.jsx
import React, {useEffect, useState} from 'react';
import {Alert, Card, Statistic, Tag, Tooltip} from 'antd';
import {
    CalendarOutlined,
    ClockCircleOutlined,
    CreditCardOutlined,
    EuroOutlined,
    InfoCircleOutlined,
    WalletOutlined
} from '@ant-design/icons';

import style from './styles/OneTimeExpensesCard.module.css';
import {instance} from "../../../../../API/API";
import ExpensesType from "./ExpensesType";
import MiddleStatistic from "./MiddleStatistic";
import PaymentsByCategory from "./PaymentsByCategory";
import dayjs from "dayjs";

const OneTimeExpensesCard = ({dateRange, selectedCategory, refreshKey}) => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [data, setData] = useState({
        total: 0,
        cash: 0,
        non_cash: 0,
        avg_monthly: 0,
        summary: {},
        by_category: [],
        top_expenses: []
    });
    const [isModalVisible, setIsModalVisible] = useState(false);

    const fetchData = async () => {
        setLoading(true);
        setError(null);

        try {
            const params = {
                date_from: dateRange[0].format('YYYY-MM-DD'),
                date_to: dayjs(dateRange[1].format('YYYY-MM-DD'),'YYYY-MM-DD').endOf('month').format('YYYY-MM-DD'),
            };

            if (selectedCategory) {
                params.category_id = selectedCategory;
            }

            const response = await instance.get(`finance_04122025/get_one_time_expenses_stats.php`, {
                params,
                withCredentials: true,
            });

            if (response.data.success) {
                setData(response.data.data);
            } else {
                throw new Error(response.data.error || 'Failed to load one-time expenses data');
            }
        } catch (err) {
            console.error('Error fetching one-time expenses:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [dateRange, selectedCategory, refreshKey]);

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('ru-RU', {
            style: 'currency',
            currency: 'EUR',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(value);
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('ru-RU');
    };

    const getPaymentTypeIcon = (type) => {
        return type === 1 ?
            <WalletOutlined className={style.cashIconSmall}/> :
            <CreditCardOutlined className={style.nonCashIconSmall}/>;
    };

    if (error) {
        return (
            <Card className={style.card}>
                <Alert
                    message="Ошибка"
                    description="Не удалось загрузить данные по разовым расходам"
                    type="error"
                    showIcon
                />
            </Card>
        );
    }

    const topExpensesContent = (
        <div className={style.topExpensesPopover}>
            <div className={style.popoverTitle}>Крупнейшие расходы</div>
            {data.top_expenses.map((expense, index) => (
                <div key={expense.id} className={style.expenseItem}>
                    <div className={style.expenseHeader}>
                        <span className={style.expenseIndex}>{index + 1}.</span>
                        <span className={style.expenseName}>{expense.name}</span>
                        {getPaymentTypeIcon(expense.payment_type)}
                    </div>
                    <div className={style.expenseDetails}>
                        <Tag color="blue" className={style.expenseCategory}>
                            {expense.category}
                        </Tag>
                        <span className={style.expenseAmount}>
                            {formatCurrency(expense.amount)}
                        </span>
                    </div>
                    {expense.description && (
                        <div className={style.expenseDescription}>
                            {expense.description}
                        </div>
                    )}
                    <div className={style.expenseDate}>
                        <CalendarOutlined/> {formatDate(expense.date)}
                    </div>
                </div>
            ))}
        </div>
    );

    return (
        <Card
            className={`${style.card} ${style.oneTimeCard}`}
            hoverable
            loading={loading}
        >
            <div className={style.cardHeader}>
                <div className={style.title}>
                    <ClockCircleOutlined className={style.icon}/>
                    <span>Разовые</span>
                </div>
                <Tooltip title="Единовременные расходы">
                    <InfoCircleOutlined className={style.infoIcon}/>
                </Tooltip>
            </div>
            <Statistic
                value={data.total}
                precision={2}
                suffix="€"
                valueStyle={{
                    color: '#fa8c16',
                    fontSize: '24px',
                    fontWeight: 600
                }}
                className={style.statistic}
            />

            <ExpensesType cash={formatCurrency(data.cash)} non_cash={formatCurrency(data.non_cash)}/>
            <MiddleStatistic summ={formatCurrency(data.avg_monthly)} period={data.period_months}/>

            <div className={style.cardFooter}>
                <div className={style.detailRow}>
                    <div className={style.detailItem}>
                        <EuroOutlined className={style.avgIcon}/>
                        <span className={style.detailLabel}>Средний чек:</span>
                        <span className={style.detailValue}>
                                {formatCurrency(data.summary.avg_expense_amount || 0)}
                            </span>
                    </div>
                </div>
            </div>


            {data.by_category.length > 0 && (
                <div className={style.cardFooter}>
                    <PaymentsByCategory setIsModalVisible={setIsModalVisible}
                                        data={data ? {
                                            ...data,
                                            by_category: data.by_category.map(category => {
                                                return {
                                                    ...category,
                                                    total: category.total_amount,
                                                    count: category.expenses_count,
                                                }

                                            }),
                                        } : []}
                                        isModalVisible={isModalVisible}
                                        formatCurrency={formatCurrency}
                    />
                </div>
            )}


        </Card>
    );
};

export default OneTimeExpensesCard;